import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, Mail } from 'lucide-react';
import {
  useOnboardTenant,
  type OnboardTenantResponse,
} from '@/api/hooks/useOnboardTenant';
import type { AppError } from '@/api/client';

const PROVINCES = [
  ['ON', 'Ontario'],
  ['QC', 'Quebec'],
  ['AB', 'Alberta'],
  ['BC', 'British Columbia'],
  ['MB', 'Manitoba'],
  ['SK', 'Saskatchewan'],
  ['NS', 'Nova Scotia'],
  ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'],
  ['PE', 'Prince Edward Island'],
  ['YT', 'Yukon'],
  ['NT', 'Northwest Territories'],
  ['NU', 'Nunavut'],
] as const;

const TIMEZONES = [
  ['America/Toronto', 'Eastern (Toronto)'],
  ['America/Winnipeg', 'Central (Winnipeg)'],
  ['America/Edmonton', 'Mountain (Edmonton)'],
  ['America/Vancouver', 'Pacific (Vancouver)'],
  ['America/Halifax', 'Atlantic (Halifax)'],
  ['America/St_Johns', 'Newfoundland (St. John\'s)'],
] as const;

const SPORTS: Array<{ code: string; label: string }> = [
  { code: 'GOLF_SIM', label: 'Golf Simulator' },
  { code: 'CRICKET', label: 'Indoor Cricket' },
  { code: 'PADEL', label: 'Padel Court' },
  { code: 'BASEBALL', label: 'Baseball Batting Cage' },
  { code: 'SIM_RACING', label: 'Racing Simulator' },
  { code: 'BADMINTON', label: 'Badminton Court' },
  { code: 'PICKLEBALL', label: 'Pickleball Court' },
  { code: 'BOWLING', label: 'Bowling' },
];

const PLAN_TIERS = ['STARTER', 'GROWTH', 'SCALE', 'ENTERPRISE'] as const;

const schema = z.object({
  businessName: z.string().min(1, 'Business name is required').max(200),
  contactEmail: z.string().email('Valid email required'),
  contactFirstName: z.string().min(1, 'First name is required'),
  contactLastName: z.string().min(1, 'Last name is required'),
  locationName: z.string().min(1, 'Location name is required').max(200),
  locationAddress: z.string().min(1, 'Street address is required'),
  locationCity: z.string().min(1, 'City is required'),
  locationProvince: z.string().min(2, 'Province is required'),
  locationTimezone: z.string().min(1, 'Timezone is required'),
  platformFeePercent: z.coerce
    .number()
    .min(0)
    .max(25)
    .default(5),
  planTier: z.enum(PLAN_TIERS).default('STARTER'),
  currency: z.string().default('CAD'),
});

type FormValues = z.input<typeof schema>;

export default function PlatformOnboardingPage() {
  const navigate = useNavigate();
  const mutation = useOnboardTenant();

  const [selectedSports, setSelectedSports] = useState<string[]>(['GOLF_SIM']);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardTenantResponse | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      locationProvince: 'ON',
      locationTimezone: 'America/Toronto',
      platformFeePercent: 5,
      planTier: 'STARTER',
      currency: 'CAD',
    },
  });

  const toggleSport = (code: string) => {
    setSelectedSports((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const onSubmit = (values: FormValues) => {
    setSubmitError(null);
    if (selectedSports.length === 0) {
      setSubmitError('Pick at least one sport — the new tenant needs something to price.');
      return;
    }
    mutation.mutate(
      {
        businessName: values.businessName,
        contactEmail: values.contactEmail,
        contactFirstName: values.contactFirstName,
        contactLastName: values.contactLastName,
        locationName: values.locationName,
        locationAddress: values.locationAddress,
        locationCity: values.locationCity,
        locationProvince: values.locationProvince,
        locationCountry: 'CA',
        locationTimezone: values.locationTimezone,
        sportCodes: selectedSports,
        platformFeePercent: Number(values.platformFeePercent),
        planTier: values.planTier,
        currency: values.currency,
      },
      {
        onSuccess: (data) => setResult(data),
        onError: (err) => {
          const e = err as unknown as AppError;
          if (e?.code === 'EMAIL_EXISTS' || e?.status === 409) {
            setSubmitError('A user with that email already exists on the platform.');
          } else if (e?.status === 400) {
            setSubmitError(e?.message ?? 'Check the form — something is invalid.');
          } else {
            setSubmitError('Onboarding failed. Please try again in a moment.');
          }
        },
      },
    );
  };

  if (result) {
    return (
      <div className="mx-auto max-w-xl px-6 py-12">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-6 w-6 flex-none text-emerald-600" aria-hidden="true" />
            <div>
              <h1 className="text-lg font-semibold text-emerald-900">Operator onboarded</h1>
              <p className="mt-1 text-sm text-emerald-800">{result.message}</p>
            </div>
          </div>
          <dl className="mt-5 space-y-1 text-sm text-emerald-900">
            <div className="flex gap-2">
              <dt className="w-28 text-emerald-700">Tenant slug</dt>
              <dd className="font-mono">{result.tenantSlug}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-28 text-emerald-700">Location</dt>
              <dd className="font-mono">{result.locationSlug}</dd>
            </div>
            <div className="flex gap-2 items-center">
              <dt className="w-28 text-emerald-700">Invite sent</dt>
              <dd className="inline-flex items-center gap-1 font-mono">
                <Mail className="h-3 w-3" aria-hidden="true" />
                {result.inviteEmailSentTo}
              </dd>
            </div>
          </dl>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate(`/platform/tenants/${result.tenantId}`)}
            className="inline-flex items-center rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a95f2f]"
          >
            View tenant →
          </button>
          <button
            type="button"
            onClick={() => {
              setResult(null);
              reset();
              setSelectedSports(['GOLF_SIM']);
            }}
            className="inline-flex items-center rounded-md border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Onboard another
          </button>
          <Link
            to="/platform"
            className="inline-flex items-center rounded-md px-4 py-2 text-sm text-neutral-500 hover:text-neutral-900"
          >
            Back to tenants
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <header className="mb-8">
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-400">
          Platform Admin
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
          Onboard new operator
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Create the tenant + first location + operator user in one shot. The operator
          receives an email with a link to set their password.
        </p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8" noValidate>
        <Fieldset title="Operator details">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Business name" error={errors.businessName?.message}>
              <input
                type="text"
                placeholder="e.g. KHEL Sports"
                {...register('businessName')}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Contact email" error={errors.contactEmail?.message}>
              <input
                type="email"
                placeholder="owner@example.com"
                autoComplete="email"
                {...register('contactEmail')}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Contact first name" error={errors.contactFirstName?.message}>
              <input type="text" {...register('contactFirstName')} className={INPUT_CLS} />
            </Field>
            <Field label="Contact last name" error={errors.contactLastName?.message}>
              <input type="text" {...register('contactLastName')} className={INPUT_CLS} />
            </Field>
          </div>
        </Fieldset>

        <Fieldset title="Initial location">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Location name" error={errors.locationName?.message}>
              <input
                type="text"
                placeholder="KHEL Sports — Pickering"
                {...register('locationName')}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Street address" error={errors.locationAddress?.message}>
              <input
                type="text"
                placeholder="1095 Kingston Rd, Unit 5A"
                {...register('locationAddress')}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="City" error={errors.locationCity?.message}>
              <input type="text" {...register('locationCity')} className={INPUT_CLS} />
            </Field>
            <Field label="Province" error={errors.locationProvince?.message}>
              <select {...register('locationProvince')} className={INPUT_CLS}>
                {PROVINCES.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name} ({code})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Timezone" error={errors.locationTimezone?.message}>
              <select {...register('locationTimezone')} className={INPUT_CLS}>
                {TIMEZONES.map(([tz, label]) => (
                  <option key={tz} value={tz}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Fieldset>

        <Fieldset
          title="Sports"
          subtitle="Pick at least one. Each selection seeds one default unit and a fallback pricing rule."
        >
          <div className="grid gap-2 md:grid-cols-2">
            {SPORTS.map((s) => (
              <label
                key={s.code}
                className="flex items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#C2713A]"
                  checked={selectedSports.includes(s.code)}
                  onChange={() => toggleSport(s.code)}
                />
                <span>{s.label}</span>
                <span className="ml-auto text-xs font-mono text-neutral-400">{s.code}</span>
              </label>
            ))}
          </div>
        </Fieldset>

        <Fieldset title="Platform config">
          <div className="grid gap-4 md:grid-cols-3">
            <Field
              label="Platform fee %"
              error={errors.platformFeePercent?.message}
            >
              <input
                type="number"
                step={0.01}
                min={0}
                max={25}
                {...register('platformFeePercent')}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Plan tier" error={errors.planTier?.message}>
              <select {...register('planTier')} className={INPUT_CLS}>
                {PLAN_TIERS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Currency" error={errors.currency?.message}>
              <select {...register('currency')} className={INPUT_CLS}>
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
              </select>
            </Field>
          </div>
        </Fieldset>

        {submitError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {submitError}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-neutral-200 pt-4">
          <Link
            to="/platform"
            className="text-sm text-neutral-500 hover:text-neutral-900"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || mutation.isPending}
            className="inline-flex items-center rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending ? 'Onboarding…' : 'Onboard operator'}
          </button>
        </div>
        <p className="text-xs text-neutral-400">
          The operator receives an email with a link to set their password. They can
          complete Stripe Connect onboarding from their admin panel after their first login.
        </p>
      </form>
    </div>
  );
}

const INPUT_CLS =
  'w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30';

function Fieldset({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-neutral-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-neutral-500">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-neutral-500">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}
